import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import Agent from "../../Network/Agent";

export default function NotificationAll() {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [perPage] = useState(20);
    const [filter, setFilter] = useState("");
    const [q, setQ] = useState("");
    const [meta, setMeta] = useState({ current_page: 1, last_page: 1, total: 0, from: 0, to: 0, total_count: 0 });
    const [rows, setRows] = useState([]);
    const [selected, setSelected] = useState({});
    const [busy, setBusy] = useState(false);
    const searchRef = useRef(null);

    const fetchData = (opts = {}) => {
        const params = {
            page: opts.page ?? page,
            per_page: perPage,
            filter: opts.filter ?? filter,
            q: opts.q ?? q,
        };
        setLoading(true);
        Agent.Notification.allPage(params)
            .then((res) => {
                const data = res.data?.data ?? {};
                setRows(data.notification ?? []);
                setMeta({
                    current_page: data.current_page ?? 1,
                    last_page: data.last_page ?? 1,
                    total: data.total ?? 0,
                    from: data.from ?? 0,
                    to: data.to ?? 0,
                    total_count: data.total_count ?? 0,
                });
                setSelected({});
            })
            .catch(() => toast.error("Failed to load notifications"))
            .finally(() => {
                setLoading(false);
                //Agent.Notification.latest(dispatch);
            });
    };

    useEffect(() => {
        fetchData({ page });
    }, [page]);

    const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
    const allChecked = rows.length > 0 && selectedIds.length === rows.length;

    const toggleAll = (checked) => {
        if (checked) {
            const next = {};
            rows.forEach((r) => { next[r.id] = true; });
            setSelected(next);
        } else {
            setSelected({});
        }
    };

    const toggleOne = (id, checked) => {
        setSelected((prev) => ({ ...prev, [id]: checked }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const value = searchRef.current?.value ?? "";
        setQ(value);
        setPage(1);
        fetchData({ page: 1, q: value });
    };

    const handleFilterChange = (val) => {
        setFilter(val);
        setPage(1);
        fetchData({ page: 1, filter: val });
    };

    const markRead = (ids) => {
        if (!ids || ids.length === 0) return;
        setBusy(true);
        Agent.Notification.markRead(ids)
            .then(() => {
                toast.success("Marked as read");
                fetchData();
            })
            .catch(() => toast.error("Failed to mark as read"))
            .finally(() => setBusy(false));
    };

    const removeItems = (ids, all = false) => {
        if (!all && (!ids || ids.length === 0)) return;
        const msg = all ? "Delete ALL your notifications?" : `Delete ${ids.length} notification(s)?`;
        if (!window.confirm(msg)) return;
        setBusy(true);
        Agent.Notification.remove(ids, all)
            .then(() => {
                toast.success("Deleted");
                if (page !== 1) setPage(1); else fetchData({ page: 1 });
            })
            .catch(() => toast.error("Failed to delete"))
            .finally(() => setBusy(false));
    };

    const markAllRead = () => {
        setBusy(true);
        Agent.Notification.readAll()
            .then(() => {
                toast.success("All marked as read");
                fetchData();
            })
            .catch(() => toast.error("Failed"))
            .finally(() => setBusy(false));
    };

    const goPage = (p) => {
        if (p < 1 || p > meta.last_page || p === meta.current_page) return;
        setPage(p);
    };

    const pageNumbers = useMemo(() => {
        const total = meta.last_page || 1;
        const current = meta.current_page || 1;
        const range = [];
        const start = Math.max(1, current - 2);
        const end = Math.min(total, current + 2);
        for (let i = start; i <= end; i++) range.push(i);
        return range;
    }, [meta]);

    return (
        <>
            <div className="row mb-2 me-1 ms-1">
                <div className="align-items-center d-flex mt-2 flex-wrap gap-2">
                    <h6 className="main-card-title mb-0 flex-grow-1">
                        Total: {meta.total} | Unread: {meta.total_count}
                    </h6>
                    <div className="flex-shrink-0">
                        <form onSubmit={handleSearch} className="input-group input-group-sm">
                            <select
                                className="form-select"
                                style={{ maxWidth: 130 }}
                                value={filter}
                                onChange={(e) => handleFilterChange(e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="unread">Unread</option>
                                <option value="read">Read</option>
                            </select>
                            <input
                                ref={searchRef}
                                defaultValue={q}
                                type="text"
                                className="form-control"
                                placeholder="Search title/body..."
                            />
                            <button className="btn btn-success" type="submit">
                                <i className="fa-light fa-search me-1" />Search
                            </button>
                            <button
                                className="btn btn-outline-primary"
                                type="button"
                                disabled={busy || selectedIds.length === 0}
                                onClick={() => markRead(selectedIds)}
                            >
                                <i className="fa-light fa-check me-1" />Mark Read ({selectedIds.length})
                            </button>
                            <button
                                className="btn btn-outline-danger"
                                type="button"
                                disabled={busy || selectedIds.length === 0}
                                onClick={() => removeItems(selectedIds)}
                            >
                                <i className="fa-light fa-trash me-1" />Delete ({selectedIds.length})
                            </button>
                            <button
                                className="btn btn-outline-secondary"
                                type="button"
                                disabled={busy}
                                onClick={markAllRead}
                            >
                                Mark all read
                            </button>
                            <button
                                className="btn btn-outline-danger"
                                type="button"
                                disabled={busy || meta.total === 0}
                                onClick={() => removeItems([], true)}
                            >
                                Delete all
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            <div className="row mb-2 mt-2 me-1 ms-1">
                <div className="col-xl-12 col-lg-12">
                    <div className="table-responsive bg-white pe-1 ps-1 pt-1">
                        <table className="table table-bordered table-striped">
                            <thead className="text-uppercase">
                                <tr>
                                    <th className="text-center toi-primary" style={{ width: 40 }}>
                                        <input
                                            type="checkbox"
                                            checked={allChecked}
                                            onChange={(e) => toggleAll(e.target.checked)}
                                        />
                                    </th>
                                    <th className="text-center toi-primary" style={{ width: 50 }}>SL</th>
                                    <th className="toi-primary">Title</th>
                                    <th className="toi-primary">Body</th>
                                    <th className="text-center toi-primary" style={{ width: 120 }}>Type</th>
                                    <th className="text-center toi-primary" style={{ width: 160 }}>Alarm Start</th>
                                    <th className="text-center toi-primary" style={{ width: 110 }}>Status</th>
                                    <th className="text-center toi-primary" style={{ width: 160 }}>Received</th>
                                    <th className="text-center toi-primary" style={{ width: 130 }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {!loading && rows.length === 0 && (
                                    <tr>
                                        <td colSpan={9} className="text-center text-muted">No notifications</td>
                                    </tr>
                                )}
                                {rows.map((row, index) => (
                                    <tr key={row.id} className={`fs-11 ${row.read_at == null ? "table-warning" : ""}`}>
                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                checked={!!selected[row.id]}
                                                onChange={(e) => toggleOne(row.id, e.target.checked)}
                                            />
                                        </td>
                                        <td className="text-center">{(meta.from || 0) + index}</td>
                                        <td>
                                            <span dangerouslySetInnerHTML={{ __html: row.title || "" }} />
                                        </td>
                                        <td>
                                            <small dangerouslySetInnerHTML={{ __html: row.body || "" }} />
                                        </td>
                                        <td className="text-center">{row.type || "-"}</td>
                                        <td className="text-center">{row.alarm_start || "-"}</td>
                                        <td className="text-center">
                                            <span className={`badge ${row.read_at ? "bg-secondary" : "bg-success"}`}>
                                                {row.read_at ? "Read" : "Unread"}
                                            </span>
                                        </td>
                                        <td className="text-center">{row.times}</td>
                                        <td className="text-center text-nowrap">
                                            {row.read_at == null && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-outline-primary me-1"
                                                    disabled={busy}
                                                    onClick={() => markRead([row.id])}
                                                    title="Mark as read"
                                                >
                                                    <i className="fa-light fa-check" />
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger"
                                                disabled={busy}
                                                onClick={() => removeItems([row.id])}
                                                title="Delete"
                                            >
                                                <i className="fa-light fa-trash" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {loading && <div className="text-center p-2">Loading......</div>}

                        {!loading && meta.last_page > 1 && (
                            <div className="d-flex justify-content-between align-items-center p-2">
                                <small className="text-muted">
                                    Showing {meta.from || 0} - {meta.to || 0} of {meta.total}
                                </small>
                                <ul className="pagination pagination-sm mb-0">
                                    <li className={`page-item ${meta.current_page === 1 ? "disabled" : ""}`}>
                                        <button className="page-link" onClick={() => goPage(1)}>«</button>
                                    </li>
                                    <li className={`page-item ${meta.current_page === 1 ? "disabled" : ""}`}>
                                        <button className="page-link" onClick={() => goPage(meta.current_page - 1)}>‹</button>
                                    </li>
                                    {pageNumbers.map((p) => (
                                        <li key={p} className={`page-item ${p === meta.current_page ? "active" : ""}`}>
                                            <button className="page-link" onClick={() => goPage(p)}>{p}</button>
                                        </li>
                                    ))}
                                    <li className={`page-item ${meta.current_page === meta.last_page ? "disabled" : ""}`}>
                                        <button className="page-link" onClick={() => goPage(meta.current_page + 1)}>›</button>
                                    </li>
                                    <li className={`page-item ${meta.current_page === meta.last_page ? "disabled" : ""}`}>
                                        <button className="page-link" onClick={() => goPage(meta.last_page)}>»</button>
                                    </li>
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
